/**
 * Scroll-linked horizontal movement with Swiper for manual swipe
 * Uses Swiper.js for smooth, native-feeling touch handling
 * Features: Infinite loop, scroll-linked animation, manual swipe support
 */

/**
 * Get Swiper from global scope (loaded via CDN before this script)
 * @returns {any}
 */
function getSwiper() {
  // @ts-ignore - Swiper is loaded via CDN
  return window.Swiper || null;
}

/**
 * Wait for Swiper if not immediately available
 * @param {number} maxAttempts
 * @returns {Promise<any>}
 */
function waitForSwiper(maxAttempts = 20) {
  return new Promise((resolve) => {
    let attempts = 0;
    const check = () => {
      const swiper = getSwiper();
      if (swiper) {
        resolve(swiper);
        return;
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(check, 100);
      } else {
        console.warn('[OurScientistNewScroll] Swiper not found after waiting');
        resolve(null);
      }
    };
    check();
  });
}

class OurScientistNewScroll {
  /**
   * @param {HTMLElement} section
   * @param {HTMLElement} swiperContainer
   */
  constructor(section, swiperContainer) {
    this.section = section;
    this.swiperContainer = swiperContainer;
    this.swiper = null;
    this.ticking = false;
    
    // Animation frame ID
    this.rafId = null;
    
    // Flag to pause scroll updates during manual swipe
    this.isManualSwiping = false;
    
    // Track cumulative scroll offset for infinite loop
    // This is the total translateX we want based on scroll
    this.scrollOffset = 0;
    
    // Track user's manual swipe offset (added to scroll offset)
    this.manualOffset = 0;
    
    // Store dimensions for calculations
    this.slideWidth = 0;
    this.scrollableDistance = 0;
    this.minTranslate = 0;
    this.maxTranslate = 0;
    
    this.init();
  }
  
  /**
   * Check if we're on mobile
   * Uses matchMedia for reliable detection in DevTools device emulation
   * @returns {boolean}
   */
  isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }
  
  /**
   * Initialize the component
   */
  async init() {
    console.log('[OurScientistNewScroll] init called, isMobile:', this.isMobile());
    
    if (!this.isMobile()) {
      // Desktop: no Swiper needed
      this.setupDesktopLayout();
      window.addEventListener('resize', () => this.handleResize(), { passive: true });
      return;
    }
    
    // Mobile: initialize Swiper
    await this.initSwiper();
    this.setupScrollListener();
    
    window.addEventListener('resize', () => this.handleResize(), { passive: true });
    window.addEventListener('orientationchange', () => {
      setTimeout(() => this.handleResize(), 100);
    });
  }
  
  /**
   * Desktop layout - disable Swiper structure
   */
  setupDesktopLayout() {
    // Swiper CSS already handles this via display: contents
  }
  
  /**
   * Initialize Swiper for mobile with infinite loop
   */
  async initSwiper() {
    console.log('[OurScientistNewScroll] initSwiper called');
    
    let SwiperClass = getSwiper();
    
    // If not immediately available, wait for it
    if (!SwiperClass) {
      console.log('[OurScientistNewScroll] Swiper not immediately available, waiting...');
      SwiperClass = await waitForSwiper();
    }
    
    console.log('[OurScientistNewScroll] Swiper loaded:', !!SwiperClass);
    if (!SwiperClass) {
      console.error('[OurScientistNewScroll] Swiper not available after waiting');
      return;
    }
    
    try {
      // @ts-ignore - Swiper constructor
      this.swiper = new SwiperClass(this.swiperContainer, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        
        // Edge spacing - first/last card have padding from screen edge
        slidesOffsetBefore: 16,
        slidesOffsetAfter: 16,
        
        // Disable loop - not enough slides for proper loop with slidesPerView: auto
        loop: false,
        
        // Free mode for smooth dragging
        freeMode: {
          enabled: true,
          momentum: true,
          momentumRatio: 0.8,
          momentumVelocityRatio: 0.6,
          momentumBounce: true,
        },
        
        // Touch settings
        touchRatio: 1,
        touchAngle: 45,
        grabCursor: true,
        resistance: true,
        resistanceRatio: 0.85,
        
        // Better emulator/mouse support
        simulateTouch: true,
        
        // Native scroll feel - don't block page scroll
        touchStartPreventDefault: false,
        touchMoveStopPropagation: false,
        
        // Only pause automatic scroll when user ACTUALLY swipes horizontally
        // touchStart does NOT pause - allows scroll-linked animation to continue
        // while user's finger is over the cards
        on: {
          // sliderFirstMove fires when horizontal movement is detected
          sliderFirstMove: () => {
            this.onManualSwipeStart();
          },
          touchEnd: () => {
            if (this.isManualSwiping) {
              this.onManualSwipeEnd();
            }
          },
          transitionEnd: () => {
            if (this.isManualSwiping) {
              this.onManualSwipeEnd();
            }
          },
        },
      });
      
      console.log('[OurScientistNewScroll] Swiper initialized:', !!this.swiper);
      console.log('[OurScientistNewScroll] Loop enabled:', this.swiper?.params?.loop);
      console.log('[OurScientistNewScroll] Slides (with clones):', this.swiper?.slides?.length);
      
      // Store slide dimensions for scroll calculations
      this.updateSlideDimensions();
      
    } catch (err) {
      console.error('[OurScientistNewScroll] Failed to initialize Swiper:', err);
    }
  }
  
  /**
   * Update slide dimension calculations
   */
  updateSlideDimensions() {
    if (!this.swiper || !this.swiper.slides || this.swiper.slides.length === 0) return;
    
    // Get first slide width including space
    const firstSlide = this.swiper.slides[0];
    if (firstSlide) {
      this.slideWidth = firstSlide.offsetWidth + 16; // slide width + spaceBetween
    }
    
    // Get translate boundaries from Swiper
    // minTranslate is the starting position (0 or slightly positive)
    // maxTranslate is the end position (large negative value)
    this.minTranslate = this.swiper.minTranslate() || 0;
    this.maxTranslate = this.swiper.maxTranslate() || 0;
    
    // Calculate the scrollable distance (positive value)
    this.scrollableDistance = Math.abs(this.maxTranslate - this.minTranslate);
    
    console.log('[OurScientistNewScroll] Slide width:', this.slideWidth);
    console.log('[OurScientistNewScroll] Min translate:', this.minTranslate);
    console.log('[OurScientistNewScroll] Max translate:', this.maxTranslate);
    console.log('[OurScientistNewScroll] Scrollable distance:', this.scrollableDistance);
  }
  
  /**
   * Clamp translate value within valid range
   * @param {number} translate
   * @returns {number}
   */
  clampTranslate(translate) {
    // maxTranslate is negative (end position)
    // minTranslate is 0 or positive (start position)
    return Math.max(this.maxTranslate, Math.min(this.minTranslate, translate));
  }
  
  /**
   * Called when user starts manually swiping
   */
  onManualSwipeStart() {
    console.log('[OurScientistNewScroll] Manual swipe START');
    this.isManualSwiping = true;
    
    // Cancel any scroll-linked animation
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }
  
  /**
   * Called when user stops manually swiping
   */
  onManualSwipeEnd() {
    console.log('[OurScientistNewScroll] Manual swipe END');
    
    if (!this.swiper) return;
    
    // Store manual offset as the difference from scroll position
    // This will be added to scroll-linked position
    const currentTranslate = this.swiper.translate || 0;
    
    // Update manual offset to include this swipe
    // We calculate the delta from where scroll-link would have put us
    this.manualOffset = currentTranslate - this.scrollOffset;
    
    console.log('[OurScientistNewScroll] Manual offset:', this.manualOffset);
    console.log('[OurScientistNewScroll] Current translate:', currentTranslate);
    
    // Resume scroll-linked animation after momentum settles
    setTimeout(() => {
      this.isManualSwiping = false;
      // Sync manual offset with current position after momentum
      if (this.swiper) {
        this.manualOffset = this.swiper.translate - this.scrollOffset;
        console.log('[OurScientistNewScroll] Final manual offset:', this.manualOffset);
      }
    }, 400);
  }
  
  /**
   * Setup scroll listener for scroll-linked animation
   */
  setupScrollListener() {
    window.addEventListener('scroll', () => {
      if (!this.ticking) {
        this.rafId = requestAnimationFrame(() => {
          this.updateScrollLinkedPosition();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }, { passive: true });
    
    // Initial position
    this.updateScrollLinkedPosition();
  }
  
  /**
   * Update horizontal position based on vertical scroll
   * Smoothly scrolls cards from start to end based on page scroll
   */
  updateScrollLinkedPosition() {
    // Don't update during manual swipe
    if (this.isManualSwiping) {
      return;
    }
    
    if (!this.swiper || !this.isMobile()) {
      return;
    }
    
    const rect = this.section.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    const sectionTop = rect.top;
    const sectionHeight = rect.height;
    const sectionBottom = rect.bottom;
    
    // Calculate scroll progress (0 to 1)
    // Start: when section bottom enters viewport
    // End: when section top exits viewport
    let scrollProgress = 0;
    
    // Section not visible yet (below viewport)
    if (sectionTop >= viewportHeight) {
      scrollProgress = 0;
    }
    // Section has scrolled past (above viewport)
    else if (sectionBottom <= 0) {
      scrollProgress = 1;
    }
    // Section is in view
    else {
      const totalRange = sectionHeight + viewportHeight;
      const scrolledDistance = viewportHeight - sectionTop;
      scrollProgress = Math.max(0, Math.min(1, scrolledDistance / totalRange));
    }
    
    // Use the actual scrollable distance from Swiper
    // This ensures we scroll through all available cards
    const scrollableDistance = this.scrollableDistance || 0;
    
    // Calculate scroll-linked offset (negative = moves left/forward)
    // Only use a portion of the full progress to make it slower
    this.scrollOffset = -scrollProgress * scrollableDistance;
    
    // Total translate = scroll offset + manual offset
    let targetTranslate = this.scrollOffset + this.manualOffset;
    
    // Clamp to valid range
    targetTranslate = this.clampTranslate(targetTranslate);
    
    // Apply using Swiper's setTranslate
    this.swiper.setTranslate(targetTranslate);
    this.swiper.updateProgress();
    this.swiper.updateSlidesClasses();
  }
  
  /**
   * Handle window resize
   */
  handleResize() {
    const wasMobile = this.swiper !== null;
    const isMobile = this.isMobile();
    
    if (isMobile && !wasMobile) {
      // Switched to mobile - initialize Swiper
      this.initSwiper();
      this.setupScrollListener();
    } else if (!isMobile && wasMobile) {
      // Switched to desktop - destroy Swiper
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      // Reset offsets
      this.scrollOffset = 0;
      this.manualOffset = 0;
    } else if (isMobile && this.swiper) {
      // Still mobile - update Swiper and recalculate dimensions
      this.swiper.update();
      this.updateSlideDimensions();
    }
  }
}

// Initialize when DOM is ready
function initOurScientistNewScroll() {
  console.log('[OurScientistNewScroll] Initializing...');
  
  const section = /** @type {HTMLElement | null} */ (
    document.querySelector('.our-scientist-new')
  );
  const swiperContainer = /** @type {HTMLElement | null} */ (
    document.querySelector('[data-scientist-swiper]')
  );
  
  console.log('[OurScientistNewScroll] Section found:', !!section);
  console.log('[OurScientistNewScroll] Swiper container found:', !!swiperContainer);
  
  if (section && swiperContainer) {
    new OurScientistNewScroll(section, swiperContainer);
  } else {
    console.warn('[OurScientistNewScroll] Missing elements, cannot initialize');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initOurScientistNewScroll);
} else {
  initOurScientistNewScroll();
}
